import { BATCH_DISMISS_DEFAULT } from '@src/providers/AppNotifications/redux/tokens';
import { MULTILEG_MARKET } from '@src/providers/OMSContext.types';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers/OrgConfigurationProvider';
import { DEFAULT_LP_SETTLEMENT_ELIGIBLE_EXCHANGE_COUNTERPARTY_NAMES } from '@src/utils/lpSettlementEligibleExchangeCounterparties';
import {
  ACTION,
  ConnectionType,
  EMPTY_ARRAY,
  includesEnumValue,
  isTalosUser,
  logger,
  parseCommaSeparatedList,
  parseJSONObject,
  ProductTypeEnum,
  ROLE,
  TransactionAutoProposalModeEnum,
  useMarketsContext,
} from '@talos/kyoko';
import { isEqual, keys } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import { SubAccountPositionsBlotterMode } from '../containers/Blotters/PositionsV3/types';
import { parseRoles } from '../containers/UserSettings/ApiKey/roleParser';
import { isUserRole, useRoleAuth } from './useRoleAuth';
import { useUser } from './useUser';

export type FeatureFlags = ReturnType<typeof useFeatureFlag>;

const DEFAULT_THRESHOLD_CURRENCIES = ['BTC', 'EUR', 'USD'];

/**
 * A hook to retrieve current values of application feature flags.
 * Add logic inside this hook for each new feature flag definition.
 *
 * @returns Object with current values of all defined feature flags
 */
export function useFeatureFlag() {
  const user = useUser();
  const { getConfig, getJSONArrayConfig, isLoaded: isOrgConfigurationLoaded } = useOrgConfiguration();
  const { isMarketOnline } = useMarketsContext();
  const { isAuthorized } = useRoleAuth();

  const featureFlags = useMemo(() => {
    const enableRestrictedTrading = getConfig(OrgConfigurationKey.EnableRestrictedTrading, false);
    const canViewCreditAndBalances = !enableRestrictedTrading || isAuthorized(ACTION.VIEW_CREDIT_AND_BALANCES);
    const enableOptionTrading = getConfig(OrgConfigurationKey.EnableOptionTrading, isTalosUser(user));
    const enableAccountSegregation = getConfig(OrgConfigurationKey.EnableAccountSegregation, false);
    const enableMarginManagementFeatures = getConfig(
      OrgConfigurationKey.EnableMarginManagementFeatures,
      enableAccountSegregation || isTalosUser(user)
    );
    const useFundDefinitionAndTradableIndexesStreams = getConfig(
      OrgConfigurationKey.UseFundDefinitionAndTradableIndexesStreams,
      false
    );
    const enableRFQSizeCounteringMultitokenCompound = getConfig(
      OrgConfigurationKey.EnableRFQSizeCounteringMultitokenCompound,
      false
    );
    const enableRFQSizeCounteringSpot = getConfig(OrgConfigurationKey.EnableRFQSizeCounteringSpot, false);

    return {
      isLoaded: isOrgConfigurationLoaded,
      enableCustomerTradingLimitsPerAccount: getConfig(OrgConfigurationKey.EnableCustomerTradingLimitsPerAccount, true),
      viewDealerMarginPages: getConfig(OrgConfigurationKey.ViewDealerMarginPages, isTalosUser(user)),
      viewCounterpartyMonitoring: getConfig(OrgConfigurationKey.ViewCounterpartyMonitoring, false),
      showDealerCustomerMarginAssetTransactionsTab: getConfig(
        OrgConfigurationKey.ShowDealerCustomerMarginAssetTransactionsTab,
        false
      ),
      viewDealerAdminAndBuyingPowerMenuItems: getConfig(OrgConfigurationKey.ViewWhitelabelMenuItems, isTalosUser(user)),
      allowModifyCustomerUserEmail: getConfig(OrgConfigurationKey.AllowModifyCustomerUserEmail, isTalosUser(user)),
      allowCustomerOptionManualExpiry: getConfig(OrgConfigurationKey.AllowCustomerOptionManualExpiry, false),
      showCustomerOrderAcceptanceRules: getConfig(
        OrgConfigurationKey.ShowCustomerOrderAcceptanceRules,
        isTalosUser(user)
      ),
      showCustomerRFQProtocolRules: getConfig(OrgConfigurationKey.ShowCustomerRFQProtocolRules, false),
      allowSyntheticRFQ: getConfig(OrgConfigurationKey.AllowSyntheticRFQ, false),
      allowSyntheticSymbolsOnManualCustomerTrade: getConfig(
        OrgConfigurationKey.AllowSyntheticSymbolsOnManualCustomerTrade,
        false
      ),
      checkCustomerBalanceOnNewCustomerTrade: getConfig(
        OrgConfigurationKey.CheckCustomerBalanceOnNewCustomerTrade,
        false
      ),
      restrictCustomerTradeByMarginEnabled: getConfig(OrgConfigurationKey.RestrictCustomerTradeByMarginEnabled, false),
      supportNotionalMultiplierInCustomerTrade: getConfig(
        OrgConfigurationKey.SupportNotionalMultiplierInCustomerTrade,
        false
      ),
      showCustomerTiering: getConfig(OrgConfigurationKey.ShowCustomerTiering, false),
      showCustomerFIXConnections: getConfig(OrgConfigurationKey.ShowCustomerFIXConnections, true),
      allowClientFIXConnectionCreation: getConfig(OrgConfigurationKey.AllowClientFIXConnectionCreation, true),
      showCustomerAccountSelectorForCustomerFIX: getConfig(
        OrgConfigurationKey.ShowCustomerAccountSelectorForCustomerFIX,
        false
      ),
      showCustomerNetAcquisition: getConfig(OrgConfigurationKey.ShowCustomerNetAcquisition, false),
      showCustomerPricingRulesBeta: getConfig(OrgConfigurationKey.ShowCustomerPricingRulesBeta, true),
      showCustomerPricingRulesLegacy: getConfig(OrgConfigurationKey.ShowCustomerPricingRulesLegacy, true),
      enableCreditLimitsPerMarketAccount: getConfig(OrgConfigurationKey.EnableCreditLimitsPerMarketAccount, true),
      // Use the status of the multileg market to indicate if we should show it's configuration screen.
      showMultiLegSecmaster: getConfig(
        OrgConfigurationKey.ShowMultiLegSecmaster,
        isMarketOnline(MULTILEG_MARKET, ConnectionType.Orders)
      ),
      showPositionsBlotterWarningColumn: getConfig(
        OrgConfigurationKey.ShowPositionsBlotterWarningColumn,
        isTalosUser(user)
      ),
      showSecurityMasterUnifiedLiquidityColumn: getConfig(
        OrgConfigurationKey.ShowSecurityMasterUnifiedLiquidityColumn,
        false
      ),
      showSecurityMasterFillIncrementColumns: getConfig(
        OrgConfigurationKey.ShowSecurityMasterFillIncrementColumns,
        false
      ),
      showSecurityMasterUseUnderlyingSecurityForRFQ: getConfig(
        OrgConfigurationKey.ShowSecurityMasterUseUnderlyingSecurityForRFQ,
        false
      ),
      enableCreateCustomSecurity: getConfig(OrgConfigurationKey.EnableCreateCustomSecurity, isTalosUser(user)),
      enableCounterpartyOverrideCustomerSecurityMaster: getConfig(
        OrgConfigurationKey.EnableCounterpartyOverrideCustomerSecurityMaster,
        true
      ),
      showCustomerDepositAddressSelection: getConfig(OrgConfigurationKey.ShowCustomerDepositAddressSelection, false),
      showCustomerDepositSourceAccount: getConfig(OrgConfigurationKey.ShowCustomerDepositSourceAccount, false),
      showCustomerWithdrawalAddresses: getConfig(OrgConfigurationKey.ShowCustomerWithdrawalAddresses, true),
      customerRequireWithdrawalApproval: getConfig(OrgConfigurationKey.CustomerRequireWithdrawalApproval, false),
      customerRequireDepositApproval: getConfig(OrgConfigurationKey.CustomerRequireDepositApproval, false),
      customerTransactionAutoProposalMode: getConfig(
        OrgConfigurationKey.CustomerTransactionAutoProposalMode,
        TransactionAutoProposalModeEnum.ProposeAndAccept
      ),
      enableMonitoringBlotters: getConfig(OrgConfigurationKey.EnableMonitoringBlotters, isTalosUser(user)),
      enableAccountSegregation,
      enableRestrictedTrading,
      enableAccountSegregationWithRestrictedTrading: enableAccountSegregation && enableRestrictedTrading,
      showTradingAggregations: enableRestrictedTrading ? isAuthorized(ACTION.VIEW_TRADING_AGGREGATIONS) : true,
      showConnectionStatusTooltip: enableRestrictedTrading ? isAuthorized(ACTION.VIEW_CONNECTION_STATUS) : true,
      canViewSecurityMaster: enableRestrictedTrading ? isAuthorized(ACTION.VIEW_SECURITY_MASTER) : true,
      enableAccountSegregationConfiguration: getConfig(
        OrgConfigurationKey.EnableAccountSegregationConfiguration,
        false
      ),
      enableTransferSubAccountFilter: getConfig(OrgConfigurationKey.EnableTransferSubAccountFilter, false),
      enableAccountSegregationShowAllMarketAccounts: getConfig(
        OrgConfigurationKey.EnableAccountSegregationShowAllMarketAccounts,
        false
      ),
      enableMarketCredentials: getConfig(OrgConfigurationKey.EnableMarketCredentials, false),
      enableOTCDeltaStrikeSupport: getConfig(OrgConfigurationKey.EnableOTCDeltaStrikeSupport, false),
      enableLoanOpenTermSupport: getConfig(OrgConfigurationKey.EnableLoanOpenTermSupport, false),
      enableDealCashflows: getConfig(OrgConfigurationKey.EnableDealCashflows, false),
      enableOTCPhysicalSettlement: getConfig(OrgConfigurationKey.EnableOTCPhysicalSettlement, false),
      supportedStrategiesForPricingModes: getConfig(
        OrgConfigurationKey.SupportedStrategiesForPricingModes,
        'Limit,Iceberg'
      )
        .split(',')
        .map(strategy => strategy.trim())
        .filter(strategy => !!strategy), // guard against trailing commas leading to empty strings
      pricingRuleAllowedCrossMarkets: getConfig(
        OrgConfigurationKey.PricingRuleAllowedCrossMarkets,
        'internal,sucden,lmax_london'
      )
        .split(',')
        .map(market => market.trim())
        .filter(market => !!market), // guard against trailing commas leading to empty strings
      pricingRuleAllowedCrossCurrencies: getConfig(
        OrgConfigurationKey.PricingRuleAllowedCrossCurrencies,
        'USD,USDT,USDC,CHF,EUR,CAD,GBP,MXN'
      )
        .split(',')
        .map(currency => currency.trim())
        .filter(currency => !!currency), // guard against trailing commas leading to empty strings
      supportedLayoutArrangements: getConfig(OrgConfigurationKey.SupportedLayoutArrangements, '')
        .split(',')
        .map(layoutArrangement => layoutArrangement.trim())
        .filter(layoutArrangement => !!layoutArrangement), // guard against trailing commas leading to empty strings
      customerSecurityAllowedUnderlyingProductTypes: getConfig(
        OrgConfigurationKey.CustomerSecurityAllowedUnderlyingProductTypes,
        'Synthetic'
      )
        .split(',')
        .map(productType => productType.trim())
        .filter(productType => !!productType) // guard against trailing commas leading to empty strings
        .filter((productType): productType is ProductTypeEnum =>
          includesEnumValue(Object.values(ProductTypeEnum), productType)
        ),
      unifiedQuoteCurrencyGroups: getConfig(OrgConfigurationKey.UnifiedQuoteCurrencyGroups, '')
        .split('|')
        .map(group =>
          group
            .split(',')
            .map(currency => currency.trim())
            .filter(currency => !!currency)
        )
        .filter(group => group.length > 0),
      enableDerivativesPositionsBlotter: getConfig(OrgConfigurationKey.EnableDerivativesPositionsBlotter, true),
      enableUnifiedPositionsBlotter: getConfig(OrgConfigurationKey.EnableUnifiedPositionsBlotter, true),
      enableLegacyPositionsTabs: getConfig(OrgConfigurationKey.EnableLegacyPositionsTabs, true),
      enableBulkClosePosition: getConfig(OrgConfigurationKey.EnableBulkClosePosition, false),
      enableBulkModifyOrders: getConfig(OrgConfigurationKey.EnableBulkModifyOrders, true),
      enableLegacyUpdateBalances: getConfig(OrgConfigurationKey.EnableLegacyUpdateBalances, false),
      enableOptionTrading,
      enableAllocationControlsPage: getConfig(OrgConfigurationKey.EnableAllocationControlsPage, isTalosUser(user)),
      // Portfolio settlement is available for Treasury users, and we also guard with an org config.
      // Both parts of the check are also just by default true available for talos users.
      enablePortfolioSettlement:
        (isUserRole(user, ROLE.TREASURY) || isTalosUser(user)) &&
        getConfig(OrgConfigurationKey.EnablePortfolioSettlement, isTalosUser(user)),
      enableLpSettlement: getConfig(OrgConfigurationKey.EnableLpSettlement, false),
      enableLpSettlementLegacy: getConfig(OrgConfigurationKey.EnableLpSettlementLegacy, false),
      // Unset config uses the default list. An explicitly empty org value (`""`) parses to `[]` and disables all exchanges.
      lpSettlementEligibleExchangeCounterparties: parseCommaSeparatedList(
        getConfig(
          OrgConfigurationKey.LPSettlementEligibleExchangeCounterparties,
          DEFAULT_LP_SETTLEMENT_ELIGIBLE_EXCHANGE_COUNTERPARTY_NAMES.join(',')
        )
      ),
      // TODO(PORT-17753): Remove after August 2026 rollout and make editable LP transfer amounts permanent.
      enableLpTransferAmount: getConfig(OrgConfigurationKey.EnableLpTransferAmount, false),
      // Sync default with backend defaultFlexibleTransferAmountAssets (portfolioruntimeconfig.go)
      flexibleTransferAmountAssets: new Set(
        parseCommaSeparatedList(getConfig(OrgConfigurationKey.FlexibleTransferAmountAssets, 'USD,EUR,GBP,JPY'))
      ),
      enableOtcSettlementWithLpSettlement: getConfig(OrgConfigurationKey.EnableOtcSettlementWithLpSettlement, false),
      freezeLpSettlementTransferTargets: getConfig(OrgConfigurationKey.FreezeLpSettlementTransferTargets, false),
      enablePortfolioManagementSystem: getConfig(
        OrgConfigurationKey.EnablePortfolioManagementSystem,
        isTalosUser(user)
      ),
      enablePMSPENavigationMigration: getConfig(OrgConfigurationKey.EnablePMSPENavigationMigration, true),
      enableDecimalAlignment: getConfig(OrgConfigurationKey.EnableDecimalAlignment, false),
      enableMarginManagementFeatures,
      // TODO(PORT-18549): Remove this flag and org config after PORT-18558 is complete.
      enablePortfolioOverviewHud: getConfig(OrgConfigurationKey.EnablePortfolioOverviewHud, false),
      enablePortfolioManagementReportsForPMSLite: getConfig(
        OrgConfigurationKey.EnablePortfolioManagementReportsForPMSLite,
        false
      ),
      enablePMSIborAborReport: getConfig(OrgConfigurationKey.EnablePMSIborAborReport, false),
      enablePMSIborAborRecon: getConfig(OrgConfigurationKey.EnablePMSIborAborRecon, false),
      enableCalendarSpreads: getConfig(OrgConfigurationKey.EnableCalendarSpreads, isTalosUser(user)),
      enablePredictionMarkets: getConfig(OrgConfigurationKey.EnablePredictionMarkets, false),
      enablePredictionMarketPriceGrid: getConfig(OrgConfigurationKey.EnablePredictionMarketPriceGrid, false),
      enableDerivativesPositionsBlotterAdvanced: getConfig(
        OrgConfigurationKey.EnableDerivativesPositionsBlotterAdvanced,
        isTalosUser(user)
      ),
      enableCurrenciesFilterSelectAll: getConfig(OrgConfigurationKey.EnableCurrenciesFilterSelectAll, false),
      subAccountPositionsBlotterMode: getConfig(
        OrgConfigurationKey.SubAccountPositionsBlotterMode,
        SubAccountPositionsBlotterMode.Standard
      ), // "Standard" or "Advanced"
      enableCustomerAccountRestrictions: getConfig(
        OrgConfigurationKey.EnableCustomerAccountRestrictions,
        isTalosUser(user)
      ),
      enableReconMismatchesBlotter: getConfig(OrgConfigurationKey.EnableReconMismatchesBlotter, isTalosUser(user)),
      enableBuyingPowerMonitoringBlotter: getConfig(
        OrgConfigurationKey.EnableBuyingPowerMonitoringBlotter,
        isTalosUser(user)
      ),
      enableAnalyticsInTrading: getConfig(OrgConfigurationKey.EnableAnalyticsInTrading, false),
      showAnalyticsMarketUptimesChart: getConfig(OrgConfigurationKey.ShowAnalyticsMarketUptimesChart, false),
      enableExecutionMarkoutsByMarketChart: getConfig(OrgConfigurationKey.EnableExecutionMarkoutsByMarketChart, false),
      enableMarketPriceChart: getConfig(OrgConfigurationKey.EnableMarketPriceChart, false),
      showBlotterPauseButton: getConfig(OrgConfigurationKey.ShowBlotterPauseButton, isTalosUser(user)),
      enableMultilegTradeFilter: getConfig(OrgConfigurationKey.EnableMultilegTradeFilter, true),
      enablePriceGridV2: getConfig(OrgConfigurationKey.EnablePriceGridV2, true),
      enableMultilegPriceGrid: getConfig(OrgConfigurationKey.EnableMultilegPriceGrid, true),
      enableCustomerPriceGridV2: getConfig(OrgConfigurationKey.EnableCustomerPriceGridV2, false),
      enableHierarchicalSubAccountPositionsBlotter: getConfig(
        OrgConfigurationKey.EnableHierarchicalSubAccountPositionsBlotter,
        true
      ),
      enableSubAccountHistoricalRecalc:
        isTalosUser(user) || getConfig(OrgConfigurationKey.EnableSubAccountHistoricalRecalc, false),
      homeCurrencyAdditionalCurrencies: getConfig(OrgConfigurationKey.HomeCurrencyAdditionalCurrencies, '')
        .split(',')
        .map(ccy => ccy.trim())
        .filter(ccy => !!ccy), // guard against trailing commas leading to empty strings
      tradingLimitsThresholdCurrencies: parseCommaSeparatedList(
        getConfig(OrgConfigurationKey.TradingLimitsThresholdCurrencies, '') || DEFAULT_THRESHOLD_CURRENCIES.join(',')
      ),
      enableCrossAssetRiskGroupPositionLimits: getConfig(
        OrgConfigurationKey.EnableCrossAssetRiskGroupPositionLimits,
        false
      ),
      enableCrossAssetRiskGroupDeltaRiskCheck: getConfig(
        OrgConfigurationKey.EnableCrossAssetRiskGroupDeltaRiskCheck,
        false
      ),
      customerBalanceShowForceWithdraw: getConfig(
        OrgConfigurationKey.CustomerBalanceShowForceWithdraw,
        isTalosUser(user)
      ),
      requireAdditionalConfirmOnBalanceTransactionPreview: getConfig(
        OrgConfigurationKey.RequireAdditionalConfirmOnBalanceTransactionPreview,
        false
      ),
      customerBalanceRequireForceWithdrawConfirm: getConfig(
        OrgConfigurationKey.CustomerBalanceRequireForceWithdrawConfirm,
        true
      ),
      enableTransactTimeInNewBalanceTransaction: getConfig(
        OrgConfigurationKey.EnableTransactTimeInNewBalanceTransaction,
        false
      ),
      enableNewCustomerBalanceTransactionDrawer: getConfig(
        OrgConfigurationKey.EnableNewCustomerBalanceTransactionDrawer,
        false
      ),
      showSubAccountPositionPnLTagColumns: getConfig(OrgConfigurationKey.ShowSubAccountPositionPnLTagColumns, false),
      showIborAborReportEndOfDayMarkColumns: getConfig(
        OrgConfigurationKey.ShowIborAborReportEndOfDayMarkColumns,
        false
      ),
      enableTradeBestExecution: getConfig(OrgConfigurationKey.EnableTradeBestExecution, true),
      enableCFDs: getConfig(OrgConfigurationKey.EnableCFDs, isTalosUser(user)),
      enableGovernmentBonds: getConfig(OrgConfigurationKey.EnableGovernmentBonds, false),
      enableShares: getConfig(OrgConfigurationKey.EnableShares, false),
      enableSEDOLDisplay: getConfig(OrgConfigurationKey.EnableSEDOLOutput, false),
      enableGovernmentBond32nds: getConfig(OrgConfigurationKey.EnableGovernmentBond32nds, false),
      enableRFQFormOptions: getConfig(OrgConfigurationKey.EnableRFQFormOptions, false),
      enableRFQFormOTCs: getConfig(OrgConfigurationKey.EnableRFQFormOTCs, false),
      enableSTSDigitalCustomOptionRFQ: getConfig(OrgConfigurationKey.EnableSTSDigitalCustomOptionRFQ, false),
      enableRFQSweeping: getConfig(OrgConfigurationKey.EnableRFQSweeping, false),
      sweepGroupIDQuotesOnlySweepable: getConfig(OrgConfigurationKey.SweepGroupIDQuotesOnlySweepable, false),
      rfqSweeperAutoCancelRemainingAfterExecution: getConfig(
        OrgConfigurationKey.RFQSweeperAutoCancelRemainingAfterExecution,
        false
      ),
      enableRFQSizeCounteringMultitokenCompound,
      enableRFQSizeCounteringSpot,
      rfqFormClearingAccountProductTypes: (
        getJSONArrayConfig<ProductTypeEnum>(OrgConfigurationKey.RFQFormClearingAccountProductTypes) ?? EMPTY_ARRAY
      ).filter((productType): productType is ProductTypeEnum =>
        includesEnumValue(Object.values(ProductTypeEnum), productType)
      ),
      // Combined flag for consumers that only need to know whether RFQ size countering is enabled for any supported product.
      enableRFQSizeCountering: enableRFQSizeCounteringSpot || enableRFQSizeCounteringMultitokenCompound,
      sizeCounteringImpliesPartialFill: getConfig(OrgConfigurationKey.SizeCounteringImpliesPartialFill, true),
      enableRfqSoundEffects: getConfig(OrgConfigurationKey.EnableRfqSoundEffects, false),
      showExchangeAddresses: getConfig(OrgConfigurationKey.ShowExchangeAddresses, false),
      enableTradingControls: getConfig(OrgConfigurationKey.EnableTradingControls, true),
      enableTradingControlsDefaultSettings: getConfig(OrgConfigurationKey.EnableTradingControlsDefaultSettings, false),
      bypassAccountSegregationChecksForTradingControlsSettings: getConfig(
        OrgConfigurationKey.BypassAccountSegregationChecksForTradingControlsSettings,
        false
      ),
      showSupportChat: getConfig(OrgConfigurationKey.ShowSupportChat, false),
      enableAvaAI: getConfig(OrgConfigurationKey.EnableAvaAI, false),
      enableOrderFormSubAccountCreation: getConfig(
        OrgConfigurationKey.EnableOrderFormSubAccountCreation,
        isTalosUser(user)
      ),
      enableSalesOrderAdvancedFeeOptions: getConfig(OrgConfigurationKey.EnableSalesOrderAdvancedFeeOptions, false),
      enableSalesRFQSubscriptionStartDate: getConfig(OrgConfigurationKey.EnableSalesRFQSubscriptionStartDate, false),
      enablePerformancePageDerivatives: getConfig(
        OrgConfigurationKey.EnablePerformancePageDerivatives,
        isTalosUser(user)
      ),
      disableCustomerMarketAccounts: getConfig(OrgConfigurationKey.DisableCustomerMarketAccounts, false),
      filterMarketAccountsByPermissionActionAll: getConfig(
        OrgConfigurationKey.FilterMarketAccountsByPermissionActionAll,
        false
      ),

      enableAutoHedging:
        getConfig(OrgConfigurationKey.EnableAutoHedging, false) && isAuthorized(ACTION.VIEW_AUTOHEDGING),
      enableDynamicDeltaHedging: enableOptionTrading && getConfig(OrgConfigurationKey.EnableDynamicDeltaHedging, false),
      enableOrderLinkInHedgeRule: getConfig(OrgConfigurationKey.EnableOrderLinkInHedgeRule, false),
      enableHedgeRuleCrossAssetRiskGroup: getConfig(OrgConfigurationKey.EnableHedgeRuleCrossAssetRiskGroup, false),
      positionHedgerHomeCurrency: getConfig(
        OrgConfigurationKey.PositionHedgerHomeCurrency,
        DEFAULT_POSITION_HEDGER_HOME_CURRENCY
      ),
      enablePortfolioSubAccountRecon: getConfig(OrgConfigurationKey.EnablePortfolioSubAccountRecon, isTalosUser(user)),
      DisableCustomerFilteringByConfigurations: getConfig(
        OrgConfigurationKey.DisableCustomerFilteringByConfigurations,
        false
      ),

      // ETF-related flags
      spreadToFixingSymbolIndexesMap: getConfig(OrgConfigurationKey.SpreadToFixingSymbolIndexesMap, JSON.stringify({})),
      enableMakerMarketplaceETFRFQFlow: getConfig(OrgConfigurationKey.EnableMakerMarketplaceETFRFQFlow, false),
      enableTakerMarketplaceETFRFQFlow: getConfig(OrgConfigurationKey.EnableTakerMarketplaceETFRFQFlow, false),
      truncateCareOrderID: getConfig(OrgConfigurationKey.TruncateCareOrderID, false),
      fixingPriceDialogMarkets: getConfig(OrgConfigurationKey.FixingPriceDialogMarkets, 'coinbase,kraken'),
      careOrderImportParser: getConfig(OrgConfigurationKey.CareOrderImportParser, 'aladdin'),
      enableCareOrderExternalIDField: getConfig(OrgConfigurationKey.EnableCareOrderExternalIDField, false),
      enableCareOrderExternalComments: getConfig(OrgConfigurationKey.EnableCareOrderExternalComments, false),
      enableImportBasketDetails: getConfig(OrgConfigurationKey.EnableImportBasketDetails, false),
      enableImportBasketDetailsUnqork: getConfig(OrgConfigurationKey.EnableImportBasketDetailsUnqork, false),
      basketImportUnderlyingSecurityMapping: parseJSONObject<string, string>(
        getConfig(OrgConfigurationKey.BasketImportUnderlyingSecurityMapping, '')
      ),
      showMakerApplyFixingDialogReferencePrice: getConfig(
        OrgConfigurationKey.ShowMakerApplyFixingDialogReferencePrice,
        false
      ),
      showMakerApplyFixingDialogIndexMarks: getConfig(OrgConfigurationKey.ShowMakerApplyFixingDialogIndexMarks, false),
      enableIndexMarkSubs: getConfig(OrgConfigurationKey.EnableIndexMarkSubs, false),
      enableCareOrderFormFundField: getConfig(OrgConfigurationKey.EnableCareOrderFormFundField, false),
      enableInKindCareOrders: getConfig(OrgConfigurationKey.EnableInKindCareOrders, false),

      careOrderInitiatingFromValues: getConfig(OrgConfigurationKey.CareOrderInitiatingFromValues, '')
        .split(',')
        .map(value => value.trim())
        .filter(value => !!value),
      repoDealPrimeBrokerValues: getConfig(OrgConfigurationKey.RepoDealPrimeBrokerValues, '')
        .split(',')
        .map(value => value.trim())
        .filter(value => !!value),
      careOrderExportMakerList: getJSONArrayConfig<string>(OrgConfigurationKey.CareOrderExportMakerList) ?? EMPTY_ARRAY,
      enableCareOrderSettleDateField: getConfig(OrgConfigurationKey.EnableCareOrderSettleDateField, false),
      supportAdjustedSettlementDate: getConfig(OrgConfigurationKey.SupportAdjustedSettlementDate, false),
      defaultBasketSettleOffsetOverride: parseJSONObject(
        getConfig(OrgConfigurationKey.DefaultBasketSettleOffsetOverride, '')
      ),
      allowCancellingCareOrderChildOrder: getConfig(OrgConfigurationKey.AllowCancellingCareOrderChildOrder, false),
      allowCancellingS2FCustomerOrder: getConfig(OrgConfigurationKey.AllowCancellingS2FCustomerOrder, false),
      enableCareOrderWriting: getConfig(OrgConfigurationKey.EnableCareOrderWriting, true),
      enableCareOrderOrders: getConfig(OrgConfigurationKey.EnableCareOrderOrders, false),
      careOrderBlockDirectExecution: getConfig(OrgConfigurationKey.CareOrderBlockDirectExecution, false),
      enableCareOrderManagement: getConfig(OrgConfigurationKey.EnableCareOrderManagement, false),
      enableCareOrderNetting: getConfig(OrgConfigurationKey.EnableCareOrderNetting, false),
      workingRequireOrderActivation: getConfig(OrgConfigurationKey.WorkingRequireOrderActivation, false),
      enableCareOrderManualFills: getConfig(OrgConfigurationKey.EnableCareOrderManualFills, false),
      enableCareOrderManualFillsConfirmationTextCopying: getConfig(
        OrgConfigurationKey.EnableCareOrderManualFillsConfirmationTextCopying,
        false
      ),
      enableTakerModifyFixing: getConfig(OrgConfigurationKey.EnableTakerModifyFixing, false),
      enableMakerModifyFixing: getConfig(OrgConfigurationKey.EnableMakerModifyFixing, false),
      enableQuotePleaseImprove: getConfig(OrgConfigurationKey.EnableQuotePleaseImprove, true),
      enableManualPricingPanelAvailableAmountDetails: getConfig(
        OrgConfigurationKey.EnableManualPricingPanelAvailableAmountDetails,
        true
      ),
      enableFlatCareOrderBlotterExport: getConfig(OrgConfigurationKey.EnableFlatCareOrderBlotterExport, false),
      enableTakerValidateFixing: getConfig(OrgConfigurationKey.EnableTakerValidateFixing, false),
      enableRFQFormFundSecurities: getConfig(OrgConfigurationKey.EnableRFQFormFundSecurities, false),
      enableTakerCopyQuoteFundTradeDetails: getConfig(OrgConfigurationKey.EnableTakerCopyQuoteFundTradeDetails, false),
      fundCareOrderDefaultSubAccounts: parseJSONObject<string, string>(
        getConfig(OrgConfigurationKey.FundCareOrderDefaultSubAccounts, '')
      ),
      enableApplyFixingDialogV2: getConfig(OrgConfigurationKey.EnableApplyFixingDialogV2, false),
      enableModifyFixingDialogV2: getConfig(OrgConfigurationKey.EnableModifyFixingDialogV2, false),
      useFundDefinitionAndTradableIndexesStreams,
      enableRFQFormFundField:
        useFundDefinitionAndTradableIndexesStreams && getConfig(OrgConfigurationKey.EnableRFQFormFundField, false),
      enableBottomUpPricingPreviews: getConfig(OrgConfigurationKey.EnableBottomUpPricingPreviews, false),
      // End ETF-related flags

      enableRFQBlotterV2: getConfig(OrgConfigurationKey.EnableRFQBlotterV2, true),
      enrichRFQBlotterV2WithCareOrders: getConfig(OrgConfigurationKey.EnrichRFQBlotterV2WithCareOrders, false),

      // Flexible UI
      enablePopoutWindows: getConfig(OrgConfigurationKey.EnablePopoutWindows, true),
      enableAdditionalTradingPanels: getConfig(OrgConfigurationKey.EnableAdditionalTradingPanels, true),
      enableLadderPanels: getConfig(OrgConfigurationKey.EnableLadderPanels, isTalosUser(user)),
      enableTradingSpaces: getConfig(OrgConfigurationKey.EnableTradingSpaces, true),
      enableMonitoringSpaces: getConfig(OrgConfigurationKey.EnableMonitoringSpaces, false),
      enableDealerSpaces: getConfig(OrgConfigurationKey.EnableDealerSpaces, false),

      // Mobile layout
      enableMobileLayout: getConfig(OrgConfigurationKey.EnableMobileLayout, false),

      requireCustomerBalancesCounterpartyFilter: getConfig(
        OrgConfigurationKey.RequireCustomerBalancesCounterpartyFilter,
        false
      ),
      requireCustomerPricingRulesCounterpartyFilter: getConfig(
        OrgConfigurationKey.RequireCustomerPricingRulesCounterpartyFilter,
        false
      ),
      loadGlobalCustomerPricingRules: getConfig(OrgConfigurationKey.LoadGlobalCustomerPricingRules, true),
      enableSecurityMasterRequiredFiltering: getConfig(
        OrgConfigurationKey.EnableSecurityMasterRequiredFiltering,
        false
      ),
      enableWalletMonitoring: getConfig(OrgConfigurationKey.EnableWalletMonitoring, isTalosUser(user)),
      enableAtlasWalletMonitoring: getConfig(OrgConfigurationKey.EnableAtlasWalletMonitoring, false),
      enableThemeTypeSelection: getConfig(OrgConfigurationKey.EnableThemeTypeSelection, true),
      enableArchiveOrders: getConfig(OrgConfigurationKey.EnableArchiveOrders, true),
      // TODO for 2.55.0 (Jan 2026 timeframe), remove this flag altogether
      enableOrderAuxData: getConfig(OrgConfigurationKey.EnableOrderAuxData, true),

      // Toggling these flags to true will use the legacy component for each Entity Admin screen
      useLegacyCounterpartyLimits: getConfig(OrgConfigurationKey.UseLegacyCounterpartyLimits, false),
      useLegacyCustomerAggregations: getConfig(OrgConfigurationKey.UseLegacyCustomerAggregations, false),
      enableNotifications: getConfig(OrgConfigurationKey.EnableNotifications, true),
      enableNotificationsDestinationsAdmin: getConfig(OrgConfigurationKey.EnableNotificationsDestinationsAdmin, true),
      dismissAllNotificationsBatchSize: getConfig(
        OrgConfigurationKey.DismissAllNotificationsBatchSize,
        BATCH_DISMISS_DEFAULT
      ),

      enableSupportModal: getConfig(OrgConfigurationKey.EnableSupportModal, true),
      enableHeaderSupportMenu: getConfig(OrgConfigurationKey.EnableHeaderSupportMenu, true),
      enableSupportModalPagerDuty: getConfig(OrgConfigurationKey.EnableSupportModalPagerDuty, true),
      allowOldMarketSelector: getConfig(OrgConfigurationKey.AllowOldMarketSelector, false),
      useNewMarketSelectorInTradingAggregations: getConfig(
        OrgConfigurationKey.UseNewMarketSelectorInTradingAggregations,
        true
      ),
      enableNewMarketSelectorMSSValidation: getConfig(OrgConfigurationKey.EnableNewMarketSelectorMSSValidation, true),
      enableInlineMultilegMarketEditing: getConfig(OrgConfigurationKey.EnableInlineMultilegMarketEditing, false),
      disableUnifiedLiquidityDerivativesMarketSelectionRestriction: getConfig(
        OrgConfigurationKey.DisableUnifiedLiquidityDerivativesMarketSelectionRestriction,
        false
      ),
      enableUnifiedLiquidityCrossInstrumentRequest: getConfig(
        OrgConfigurationKey.EnableUnifiedLiquidityCrossInstrumentRequest,
        false
      ),
      enableUnifiedLiquidityMarketSymbols: getConfig(OrgConfigurationKey.EnableUnifiedLiquidityMarketSymbols, false),
      unifiedLiquidityCombinePerpsAndCFDs: getConfig(OrgConfigurationKey.UnifiedLiquidityCombinePerpsAndCFDs, false),

      enableNetAllPosition: getConfig(OrgConfigurationKey.EnableNetAllPosition, false),
      creditBlotterCalculationTypes: parseCommaSeparatedList(
        getConfig(OrgConfigurationKey.CreditBlotterCalculationTypes, 'BuyingPower')
      ),
      enableTradePredictions: getConfig(OrgConfigurationKey.EnableTradePredictions, isTalosUser(user)),
      enablePreTrade: getConfig(OrgConfigurationKey.EnablePreTrade, false),
      enableOrderModificationDiffs: getConfig(OrgConfigurationKey.EnableOrderModificationDiffs, false),

      disableBlotterErrorBanner: getConfig(OrgConfigurationKey.DisableBlotterErrorBanner, false),

      enableUnifiedLiquiditySelfServe: getConfig(OrgConfigurationKey.EnableUnifiedLiquiditySelfServe, true),
      enableSettleGroupValuesColumns: getConfig(OrgConfigurationKey.EnableSettleGroupValuesColumns, isTalosUser(user)),
      enableNegotiatedRFQFlow: getConfig(OrgConfigurationKey.EnableNegotiatedRFQFlow, false),
      enableExecutingOtherUserNegotiatedRFQ: getConfig(
        OrgConfigurationKey.EnableExecutingOtherUserNegotiatedRFQ,
        false
      ),
      enablePublishCustomRate: getConfig(OrgConfigurationKey.EnablePublishCustomRate, true),
      disableNonSTFNegotiatedRFQ: getConfig(OrgConfigurationKey.DisableNonSTFNegotiatedRFQ, false),
      showStFMarketsInNonStFNegotiatedRFQ: getConfig(OrgConfigurationKey.ShowStFMarketsInNonStFNegotiatedRFQ, false),
      showDeprecatedAdminTools: getConfig(OrgConfigurationKey.ShowDeprecatedAdminTools, false),
      enableNonNativeOHLCResolutions: getConfig(OrgConfigurationKey.EnableNonNativeOHLCResolutions, false),
      enableOrderTrajectoryCharts: getConfig(OrgConfigurationKey.EnableOrderTrajectoryCharts, false),
      enableOrderTrajectoryPredictions: getConfig(OrgConfigurationKey.EnableOrderTrajectoryPredictions, false),
      enableOTCSupport: getConfig(OrgConfigurationKey.EnableOTCSupport, false),
      enableOTCCustomerCounterpartySupport: getConfig(OrgConfigurationKey.EnableOTCCustomerCounterpartySupport, false),
      disableCrossCurrencyQty: getConfig(OrgConfigurationKey.DisableCrossCurrencyQty, true),
      nonSpotDelta1DefaultQty: getConfig(OrgConfigurationKey.NonSpotDelta1DefaultQty, 'Base'),
      enableIWouldAnalytics: getConfig(OrgConfigurationKey.EnableIWouldAnalytics, true),
      enableDailyAnalytics: getConfig(OrgConfigurationKey.EnableDailyAnalytics, false) || isTalosUser(user), // Talos users will always be able to see Daily Analytics
      dailyAnalyticsTrialEndDate: getConfig(OrgConfigurationKey.DailyAnalyticsTrialEndDate, ''),
      enableCreditBlotter: getConfig(OrgConfigurationKey.EnableCreditBlotter, true) && canViewCreditAndBalances,
      enableTransfersBlotter: getConfig(OrgConfigurationKey.EnableTransfersBlotter, true),
      enableOptionsChainView: getConfig(OrgConfigurationKey.EnableOptionsChainView, true),
      enableUserAPIKey: getConfig(OrgConfigurationKey.EnableUserAPIKey, true),
      enableBalancesBlotter: getConfig(OrgConfigurationKey.EnableBalancesBlotter, true) && canViewCreditAndBalances,
      enableDelta1BPSPricingMode: getConfig(OrgConfigurationKey.EnableDelta1BPSPricingMode, true),
      enableDurationSubmission: getConfig(OrgConfigurationKey.EnableDurationSubmission, false),
      enableOnlyCareOrderRelatedOrderSubmission: getConfig(
        OrgConfigurationKey.EnableOnlyCareOrderRelatedOrderSubmission,
        false
      ),
      enableTreasuryLinkAccountFiltering: getConfig(OrgConfigurationKey.EnableTreasuryLinkAccountFiltering, false),
      clientLogoName: getConfig(OrgConfigurationKey.ClientLogoName, ''),
      clientTitle: getConfig(OrgConfigurationKey.ClientTitle, ''),
      isRetailScale: getConfig(OrgConfigurationKey.RetailScale, false),
      canEditOwnRoles: getConfig(OrgConfigurationKey.AdminUserAdminCanEditOwnRoles, true),
      enableServiceAccounts: getConfig(OrgConfigurationKey.EnableServiceAccounts, false),
      canViewUserAPIKey: getConfig(OrgConfigurationKey.AdminUserAdminViewUserAPIKey, false),
      canEditUserAPIKey: getConfig(OrgConfigurationKey.AdminUserAdminEditUserAPIKey, false),
      canAdminUserAPIKey: getConfig(OrgConfigurationKey.AdminUserAdminAdminUserAPIKey, false),
      enablePortfolioComplianceAdminRole: getConfig(OrgConfigurationKey.EnablePortfolioComplianceAdminRole, false),
      userAPIKeyCanAddRoles: parseRoles(getConfig(OrgConfigurationKey.AdminUserAPIKeyCanAddRoles, '')),
      adminCredentialsWhitelist: parseCommaSeparatedList(getConfig(OrgConfigurationKey.AdminCredentialsWhitelist, '')),
      adminCredentialsBlacklist: parseCommaSeparatedList(getConfig(OrgConfigurationKey.AdminCredentialsBlacklist, '')),
      blockedSubscriptions: parseCommaSeparatedList(getConfig(OrgConfigurationKey.BlockSubscriptions, '')),
      enableHaircutRatio: getConfig(OrgConfigurationKey.EnableHaircutRatio, false),
      enableNetworkTwo: getConfig(OrgConfigurationKey.EnableNetworkTwo, false),
      canViewCreditAndBalances,
      enableRepoProduct: getConfig(OrgConfigurationKey.EnableRepoProduct, false),
      enableSignals:
        getConfig(OrgConfigurationKey.EnableSignals, false) &&
        getConfig(OrgConfigurationKey.CoinMetricsApiKey, '').trim().length > 0,
      requireMobileUITraderRole: getConfig(OrgConfigurationKey.RequireMobileUITraderRole, false),
      pmsOverviewTopUnderlyingCount: (() => {
        const raw = getConfig(
          OrgConfigurationKey.PMSOverviewTopUnderlyingCount,
          DEFAULT_PMS_OVERVIEW_TOP_UNDERLYING_COUNT
        );
        return raw >= 1 ? Math.floor(raw) : DEFAULT_PMS_OVERVIEW_TOP_UNDERLYING_COUNT;
      })(),
      maxRecentTransferAccounts: (() => {
        const raw = getConfig(OrgConfigurationKey.MaxRecentTransferAccounts, DEFAULT_MAX_RECENT_TRANSFER_ACCOUNTS);
        return raw >= 1 && raw <= MAX_RECENT_TRANSFER_ACCOUNTS_LIMIT
          ? Math.floor(raw)
          : DEFAULT_MAX_RECENT_TRANSFER_ACCOUNTS;
      })(),
    };
  }, [isOrgConfigurationLoaded, getConfig, getJSONArrayConfig, user, isMarketOnline, isAuthorized]);

  const previousFeatureFlags = useRef<typeof featureFlags | undefined>(undefined);
  useEffect(() => {
    const prev = previousFeatureFlags.current;
    const next = featureFlags;
    const allKeys = new Set([...keys(prev), ...keys(next)]);
    for (const key of allKeys) {
      if (prev == null || !isEqual(prev[key as keyof typeof prev], next[key as keyof typeof next])) {
        logger.trackFeatureFlag(key, next[key as keyof typeof next]);
      }
    }
    previousFeatureFlags.current = featureFlags;
  }, [featureFlags]);

  return featureFlags;
}

export const DEFAULT_POSITION_HEDGER_HOME_CURRENCY = 'USD';
export const DEFAULT_PMS_OVERVIEW_TOP_UNDERLYING_COUNT = 5;
export const DEFAULT_MAX_RECENT_TRANSFER_ACCOUNTS = 3;
export const MAX_RECENT_TRANSFER_ACCOUNTS_LIMIT = 20;
